import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { Check, Print } from "@material-ui/icons";
import { EpocusBillingState } from "elephant-web/gql";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../../_lib/use-active-program";
import { linkToEpocusBilling } from "../../../links";
import {
  useEpocusBillingDayQuery,
  useEpocusBillingReportQuery,
} from "../../_lib/__generated__/billing-operations.gql";
import {
  BillingCrumb,
  BillingPageHeading,
  BillingPageRoot,
} from "../../_lib/billing-chrome";
import { BillingReportCard } from "../../_lib/billing-report-card";
import { BILLING_REPORT_PRINT_CSS } from "../../_lib/report-print";
import { todayIsoDay, toServiceDate } from "../../_lib/service-day";

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const Actions = styled(Box)({
  display: "flex",
  gap: 11,
  flexWrap: "wrap",
  "@media print": { display: "none" },
});

const CompliantChip = styled(Typography)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  alignSelf: "flex-start",
  fontSize: rem(13),
  fontWeight: 700,
  color: EPOCUS_STATUS_REVIEWED_TEXT,
  backgroundColor: EPOCUS_STATUS_REVIEWED_BG,
  padding: "6px 12px",
  borderRadius: 999,
});

export const EpocusBillingReportPage: FC = () => {
  const navigate = useNavigate();
  const { examId } = useParams<{ examId: string }>();
  const [params] = useSearchParams();
  const isoDay = params.get("date") ?? todayIsoDay();

  const { programs } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;

  const { data, loading, error } = useEpocusBillingReportQuery({
    variables: { programId: programId ?? "", examId: examId ?? "" },
    skip: !programId || !examId,
    fetchPolicy: "cache-and-network",
  });

  const { data: dayData } = useEpocusBillingDayQuery({
    variables: {
      programId: programId ?? "",
      serviceDate: toServiceDate(isoDay),
    },
    skip: !programId,
  });

  const report = data?.epocusBillingReport;
  const accessionNumber =
    [
      ...(dayData?.epocusBillingDay.billable ?? []),
      ...(dayData?.epocusBillingDay.heldBack ?? []),
    ].find((row) => row.examId === examId)?.accessionNumber ?? null;
  const backToBilling = `${linkToEpocusBilling()}?date=${isoDay}`;

  return (
    <BillingPageRoot>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: a static print
          stylesheet with no interpolated input — print-to-PDF is the demo's
          PDF export and needs rules scoped to this page only. */}
      <style dangerouslySetInnerHTML={{ __html: BILLING_REPORT_PRINT_CSS }} />

      <Box style={{ display: "flex" }}>
        <BillingCrumb to={backToBilling} label="Back to billing" />
      </Box>

      <BillingPageHeading
        title="Compliant report"
        subtitle="What your billing company receives — everything a payer needs, no EHR integration required."
      />

      {loading && !report ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading the report" />
        </LoadingRow>
      ) : error || !report ? (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          Couldn't load this report.
        </Typography>
      ) : (
        <>
          {(report.billingStatus === EpocusBillingState.ReadyToBill ||
            report.billingStatus === EpocusBillingState.Exported) && (
            <CompliantChip>
              <Check style={{ fontSize: rem(17) }} />
              Ready to bill — all required elements present
            </CompliantChip>
          )}

          <BillingReportCard
            report={report}
            programName={program?.name ?? "Your program"}
            accessionNumber={accessionNumber}
          />

          <Actions>
            <Button variant="outlined" onClick={() => navigate(backToBilling)}>
              Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.print()}
            >
              Print / Save PDF
            </Button>
          </Actions>
        </>
      )}
    </BillingPageRoot>
  );
};
